import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/elements/button';
import { Search, Add, ExportIcon } from '../../assets/icons';
import '../../assets/css/admin/products.css';
import apiConfig from '../../config/api_config';
import LoadingSpinner from '../../components/elements/LoadingSpinner';

const AdminProducts = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        category: 'all',
        vendor: 'all',
        status: 'all'
    });

    const [products, setProducts] = useState([]);

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiConfig.get('/products/');
            if (response?.data?.data) {
                setProducts(response.data.data);
            } else {
                setProducts([]);
            }
        } catch (err) {
            console.error('Failed to fetch products:', err);
            setError('Failed to load products');
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    // Calculate stats dynamically
    const stats = useMemo(() => {
        const totalProducts = products.length;
        const activeProducts = products.filter(p => p.is_active !== false).length;
        const lowStock = products.filter(p => (p.stock || p.quantity || 0) < 10).length;
        return { totalProducts, activeProducts, lowStock };
    }, [products]);

    // Filter products
    const filteredProducts = useMemo(() => {
        return products.filter(product => {
            const searchMatch = 
                product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.description?.toLowerCase().includes(searchTerm.toLowerCase());
            
            const categoryMatch = filters.category === 'all' || 
                product.category?.name?.toLowerCase().includes(filters.category);
            
            const statusMatch = filters.status === 'all' || 
                (filters.status === 'active' && product.is_active !== false) ||
                (filters.status === 'inactive' && product.is_active === false);
            
            return searchMatch && categoryMatch && statusMatch;
        });
    }, [products, searchTerm, filters]);

    if (loading) {
        return (
            <div className="admin-products">
                <div className="loading-state">
                    <LoadingSpinner />
                    <p>Loading products...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-products">
                <div className="error-state">
                    <p>{error}</p>
                    <Button text="Retry" onClick={fetchProducts} />
                </div>
            </div>
        );
    }

    return (
        <div className="admin-products">
            <div className="page-header">
                <div className="header-content">
                    <h1>Product Management</h1>
                    <p>Manage and monitor product listings</p>
                </div>
                <div className="header-actions">
                    <Button
                        icon={<img src={ExportIcon} alt="" />}
                        text="Export Catalog"
                        variant="outline"
                    />
                    <Link to="/admin/products/new">
                        <Button
                            icon={<img src={Add} alt="" />}
                            text="Add Product"
                            variant="primary"
                        />
                    </Link>
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <h3>Total Products</h3>
                    <div className="stat-value">{stats.totalProducts}</div>
                </div>
                <div className="stat-card">
                    <h3>Active Listings</h3>
                    <div className="stat-value success">{stats.activeProducts}</div>
                </div>
                <div className="stat-card">
                    <h3>Low Stock Alert</h3>
                    <div className="stat-value warning">{stats.lowStock}</div>
                </div>
            </div>

            <div className="filters-section">
                <div className="search-bar">
                    <img src={Search} alt="" />
                    <input
                        type="text"
                        placeholder="Search products by name, ID or vendor..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="filters">
                    <select
                        value={filters.category}
                        onChange={(e) => setFilters({...filters, category: e.target.value})}
                    >
                        <option value="all">All Categories</option>
                        <option value="feed">Feed</option>
                        <option value="medicine">Medicine</option>
                        <option value="equipment">Equipment</option>
                    </select>

                    <select
                        value={filters.vendor}
                        onChange={(e) => setFilters({...filters, vendor: e.target.value})}
                    >
                        <option value="all">All Vendors</option>
                        <option value="aqua">AquaSupplies Ltd</option>
                        <option value="fish">FishTech Store</option>
                    </select>

                    <select
                        value={filters.status}
                        onChange={(e) => setFilters({...filters, status: e.target.value})}
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="draft">Draft</option>
                    </select>
                </div>
            </div>

            <div className="products-grid">
                {filteredProducts.length === 0 ? (
                    <div className="empty-state">
                        <p>No products found</p>
                    </div>
                ) : (
                    filteredProducts.map(product => (
                    <div key={product.id} className="product-card">
                        <div className="product-thumbnail">
                            {product.image ? (
                                <img src={product.image} alt={product.name} />
                            ) : (
                                <div className="no-image">No Image</div>
                            )}
                            <span className={`status ${product.is_active !== false ? 'active' : 'inactive'}`}>
                                {product.is_active !== false ? 'Active' : 'Inactive'}
                            </span>
                        </div>

                        <div className="product-content">
                            <div className="product-header">
                                <h3>{product.name}</h3>
                                <span className="product-id">PRD{String(product.id).padStart(3, '0')}</span>
                            </div>

                            <div className="vendor-info">
                                <span className="vendor">{product.vendor?.store_name || 'N/A'}</span>
                                <span className="category">{product.category?.name || 'Uncategorized'}</span>
                            </div>

                            <div className="product-meta">
                                <div className="price">₦{parseFloat(product.price || 0).toLocaleString()}</div>
                            </div>

                            <div className="stock-info">
                                <div className="stock-level">
                                    <label>Stock Level</label>
                                    <span className={(product.stock || product.quantity || 0) < 10 ? 'low' : ''}>
                                        {product.stock || product.quantity || 0} units
                                    </span>
                                </div>
                            </div>

                            {product.description && (
                                <div className="product-description">
                                    <p>{product.description.substring(0, 100)}...</p>
                                </div>
                            )}
                        </div>

                        <div className="card-actions">
                            <Link to={`/admin/products/${product.id}`}>
                                <Button
                                    text="View Details"
                                    variant="outline"
                                />
                            </Link>
                            <Link to={`/admin/products/${product.id}/edit`}>
                                <Button
                                    text="Edit Product"
                                    variant="primary"
                                />
                            </Link>
                        </div>
                    </div>
                )))}
            </div>
        </div>
    );
};

export default AdminProducts;